// Dependency-free rich text: a contentEditable composer + a sanitized renderer.
//
// RichTextEditor uses document.execCommand — deprecated on paper but supported
// by every shipping browser, and it keeps this WYSIWYG at zero dependencies
// (the project rule). It emits an HTML string; emptiness must be judged on the
// TEXT content (an "empty" editor still contains <br> markup) via richTextIsEmpty.
//
// RichBody renders stored comment HTML through sanitizeRichHtml: script/iframe/
// object/embed/form/style/meta/link tags are removed outright, every on*=
// handler attribute is dropped, and javascript: URLs are neutralized — the same
// posture as the portal's KB renderer. Legacy plain-text comments (no markup)
// should NOT go through RichBody; use looksLikeHtml to decide.

import { useEffect, useRef, useState } from "react";

/** Roster entry for @mention autocomplete. */
export interface MentionUser {
  id: string;
  name: string;
}

/** True when the string contains a recognized HTML element — anywhere, not just
 *  at the start. Email/AI-authored bodies often lead with text ("Dear …,<br>…"),
 *  so a starts-with-"<" test would render their markup as literal tags. We match
 *  an opening/closing/self-closing tag from the same allowlist the sanitizer
 *  honors, so stray "a < b" prose is NOT mistaken for HTML. */
let htmlTagRe: RegExp | null = null;
export function looksLikeHtml(s: string): boolean {
  // Built lazily so it can reference ALLOWED_TAGS (declared below) without a
  // module-init temporal-dead-zone error.
  htmlTagRe ??= new RegExp(`<\\s*/?\\s*(?:${[...ALLOWED_TAGS].join("|")})(?:\\s[^>]*)?/?>`, "i");
  return htmlTagRe.test(s);
}

/** True when the HTML has no visible text content (e.g. just <br>). Parsed via
 *  DOMParser (an inert document) rather than assigning innerHTML to a live node,
 *  so untrusted markup never triggers resource loads or risks leaking live. */
export function richTextIsEmpty(html: string): boolean {
  const doc = new DOMParser().parseFromString(html, "text/html");
  return (doc.body.textContent ?? "").trim().length === 0;
}

/** A URL is safe to keep on href/src if it is relative/anchor (no scheme) or
 *  uses an allowlisted scheme. Whitespace + control chars are collapsed first so
 *  obfuscated variants (java\tscript:, java\nscript:) can't slip past. */
function isSafeUrl(value: string): boolean {
  const v = value.replace(/\s+/g, "").toLowerCase();
  const scheme = v.match(/^([a-z][a-z0-9+.-]*):/);
  if (!scheme) return true; // relative path, anchor, or fragment
  return scheme[1] === "http" || scheme[1] === "https" || scheme[1] === "mailto";
}

/** Tags allowed to survive sanitization. Anything else is UNWRAPPED (its text
 *  kept, the element dropped) so the renderer is a strict allowlist, not a
 *  denylist — unknown/future elements can't smuggle script. */
const ALLOWED_TAGS = new Set([
  "a", "b", "strong", "i", "em", "u", "s", "strike", "del", "ins", "mark",
  "p", "br", "hr", "span", "div", "blockquote", "code", "pre",
  "ul", "ol", "li", "h1", "h2", "h3", "h4", "h5", "h6",
  "table", "thead", "tbody", "tfoot", "tr", "td", "th",
]);

/** Strip active content from stored HTML before rendering it. Strict allowlist:
 *  dangerous containers (incl. svg/math/foreignObject — common mXSS vectors) are
 *  removed outright; unknown tags are unwrapped; event handlers, inline styles
 *  (clickjacking / CSS exfiltration), and any non-http(s)/mailto URL
 *  (javascript:, data:, …) are dropped. */
export function sanitizeRichHtml(html: string): string {
  const doc = new DOMParser().parseFromString(html, "text/html");
  // Remove script-bearing / namespace-confusion containers and their subtrees.
  doc
    .querySelectorAll("script, iframe, object, embed, form, style, meta, link, base, svg, math, template, noscript")
    .forEach((el) => el.remove());
  // querySelectorAll returns a STATIC list, so mutating the tree mid-walk is safe.
  doc.body.querySelectorAll("*").forEach((el) => {
    if (!ALLOWED_TAGS.has(el.tagName.toLowerCase())) {
      // Unwrap: keep the (already-listed) children, drop the unknown element.
      el.replaceWith(...el.childNodes);
      return;
    }
    for (const attr of [...el.attributes]) {
      const name = attr.name.toLowerCase();
      if (name.startsWith("on") || name === "style" || name === "srcdoc" || name.startsWith("form")) {
        el.removeAttribute(attr.name);
        continue;
      }
      if ((name === "href" || name === "src" || name === "xlink:href") && !isSafeUrl(attr.value)) {
        el.removeAttribute(attr.name);
      }
    }
  });
  return doc.body.innerHTML;
}

/** Sanitized renderer for rich comment bodies. */
export function RichBody({ html }: { html: string }) {
  return <div className="rich-body" dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(html) }} />;
}

const TOOLS: { cmd: string; label: string; title: string; arg?: () => string | null }[] = [
  { cmd: "bold", label: "B", title: "Bold (Ctrl+B)" },
  { cmd: "italic", label: "I", title: "Italic (Ctrl+I)" },
  { cmd: "underline", label: "U", title: "Underline (Ctrl+U)" },
  { cmd: "insertUnorderedList", label: "•", title: "Bulleted list" },
  { cmd: "insertOrderedList", label: "1.", title: "Numbered list" },
  {
    cmd: "createLink",
    label: "🔗",
    title: "Insert link",
    arg: () => {
      const url = window.prompt("Link URL (https://…)");
      if (!url) return null;
      return /^https?:\/\//i.test(url) ? url : `https://${url}`;
    },
  },
  { cmd: "removeFormat", label: "⌫", title: "Clear formatting" },
];

/** WYSIWYG composer. Controlled-ish: `value` resets the editor when it changes
 *  to "" (post-submit clear); user input flows out through onChange(html). */
export function RichTextEditor({
  value,
  onChange,
  placeholder,
  disabled,
  insert,
  mentionRoster,
}: {
  value: string;
  onChange: (html: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Imperative insert: bump `nonce` to splice `html` at the caret (or append
   *  to the end when the caret isn't inside the editor). Used by e.g. the ticket
   *  composer's canned-response picker. */
  insert?: { html: string; nonce: number };
  /** When provided, typing "@" opens an autocomplete of these users; picking one
   *  inserts "@Full Name". Off (undefined) leaves the editor exactly as before. */
  mentionRoster?: MentionUser[];
}) {
  const ref = useRef<HTMLDivElement>(null);

  // ----- @mention autocomplete (only when mentionRoster is supplied) -----
  // `mq` holds the active query ("" right after typing @) + where the '@' sits
  // in the current text node, so a pick can replace exactly that token.
  const [mq, setMq] = useState<{ query: string; node: Text; at: number } | null>(null);
  const [mIdx, setMIdx] = useState(0);
  const candidates =
    mentionRoster && mq
      ? mentionRoster
          .filter((u) => u.name.toLowerCase().includes(mq.query.toLowerCase()))
          .slice(0, 6)
      : [];

  // Read the caret; if it sits right after an "@token", surface that query.
  function refreshMentionQuery() {
    if (!mentionRoster) return;
    const el = ref.current;
    const sel = window.getSelection();
    if (!el || !sel || sel.rangeCount === 0 || !sel.isCollapsed) return setMq(null);
    const range = sel.getRangeAt(0);
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE || !el.contains(node)) return setMq(null);
    const textBefore = (node.textContent ?? "").slice(0, range.startOffset);
    const m = textBefore.match(/@([^\s@]{0,30})$/);
    if (!m) return setMq(null);
    setMq({ query: m[1] ?? "", node: node as Text, at: range.startOffset - m[0].length });
    setMIdx(0);
  }

  function insertMention(u: MentionUser) {
    const el = ref.current;
    if (!el || !mq) return;
    const node = mq.node;
    // The captured text node may have been detached / normalized since (a
    // re-render or blur). Bail safely rather than throw or corrupt the DOM.
    if (!node.isConnected || !el.contains(node)) {
      setMq(null);
      return;
    }
    try {
      const text = node.textContent ?? "";
      const sel = window.getSelection();
      // Only trust the live caret offset when it's still inside this node.
      const caret =
        sel && sel.rangeCount > 0 && sel.getRangeAt(0).startContainer === node
          ? sel.getRangeAt(0).startOffset
          : text.length;
      const before = text.slice(0, mq.at);
      const after = text.slice(caret);
      node.textContent = `${before}@${u.name} ${after}`;
      const newOffset = before.length + 1 + u.name.length + 1;
      const r = document.createRange();
      r.setStart(node, Math.min(newOffset, (node.textContent ?? "").length));
      r.collapse(true);
      sel?.removeAllRanges();
      sel?.addRange(r);
      onChange(el.innerHTML);
    } catch {
      /* selection/range edge cases — never crash the composer */
    }
    setMq(null);
  }

  // Seed the editor ONCE on mount with any initial value (e.g. editing an
  // existing KB article body). Comment composers mount with value="" so this is
  // a no-op for them. Runs mount-only so it never fights the caret.
  useEffect(() => {
    if (ref.current && value && ref.current.innerHTML === "") {
      ref.current.innerHTML = sanitizeRichHtml(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Imperative insert (canned responses): splice html at the caret when it's
  // inside the editor, else append. Fires onChange so the owner's value tracks.
  const lastInsert = useRef(0);
  useEffect(() => {
    const el = ref.current;
    if (!insert || !el || insert.nonce === lastInsert.current) return;
    lastInsert.current = insert.nonce;
    el.focus();
    const sel = window.getSelection();
    const caretInside = sel && sel.rangeCount > 0 && el.contains(sel.anchorNode);
    if (caretInside) {
      document.execCommand("insertHTML", false, sanitizeRichHtml(insert.html));
    } else {
      el.innerHTML = sanitizeRichHtml(el.innerHTML + insert.html);
    }
    onChange(el.innerHTML);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [insert?.nonce]);

  // Clear the editor when the owner resets value to "" (after a successful
  // post). We deliberately do NOT mirror every keystroke back into innerHTML —
  // that would reset the caret position on each input.
  useEffect(() => {
    if (ref.current && value === "" && ref.current.innerHTML !== "") {
      ref.current.innerHTML = "";
    }
  }, [value]);

  function exec(cmd: string, argFn?: () => string | null) {
    const arg = argFn ? argFn() : undefined;
    if (argFn && arg === null) return; // prompt cancelled
    ref.current?.focus();
    document.execCommand(cmd, false, arg ?? undefined);
    if (ref.current) onChange(ref.current.innerHTML);
  }

  return (
    <div className={`rt${disabled ? " rt-disabled" : ""}`}>
      <div className="rt-toolbar" role="toolbar" aria-label="Text formatting">
        {TOOLS.map((t) => (
          <button
            key={t.cmd}
            type="button"
            className={`rt-btn rt-${t.cmd}`}
            title={t.title}
            aria-label={t.title}
            disabled={disabled}
            // preventDefault on mousedown keeps the editor selection alive.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => exec(t.cmd, t.arg)}
          >
            {t.label}
          </button>
        ))}
      </div>
      <div className="rt-editor-wrap">
        <div
          ref={ref}
          className="rt-editor"
          contentEditable={!disabled}
          role="textbox"
          aria-multiline="true"
          aria-label={placeholder ?? "Rich text"}
          data-placeholder={placeholder ?? ""}
          onInput={() => {
            if (ref.current) onChange(ref.current.innerHTML);
            refreshMentionQuery();
          }}
          onKeyUp={(e) => {
            // Arrow/Home/End move the caret without an input event — re-check.
            if (e.key.startsWith("Arrow") || e.key === "Home" || e.key === "End") refreshMentionQuery();
          }}
          onKeyDown={(e) => {
            if (!mq || candidates.length === 0) return;
            if (e.key === "ArrowDown") {
              e.preventDefault();
              setMIdx((i) => (i + 1) % candidates.length);
            } else if (e.key === "ArrowUp") {
              e.preventDefault();
              setMIdx((i) => (i - 1 + candidates.length) % candidates.length);
            } else if (e.key === "Enter" || e.key === "Tab") {
              // Pick the highlighted mention instead of inserting a newline / sending.
              e.preventDefault();
              e.stopPropagation();
              insertMention(candidates[Math.min(mIdx, candidates.length - 1)]!);
            } else if (e.key === "Escape") {
              e.preventDefault();
              setMq(null);
            }
          }}
          onBlur={() => setTimeout(() => setMq(null), 150)}
          suppressContentEditableWarning
        />
        {mq && candidates.length > 0 ? (
          <ul className="mention-pop" role="listbox" aria-label="Mention a person">
            {candidates.map((u, i) => (
              <li key={u.id}>
                <button
                  type="button"
                  role="option"
                  aria-selected={i === mIdx}
                  className={`mention-opt${i === mIdx ? " active" : ""}`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => insertMention(u)}
                >
                  @{u.name}
                </button>
              </li>
            ))}
          </ul>
        ) : null}
      </div>
    </div>
  );
}
